<script lang="ts" module>
  import { SvelteSet } from 'svelte/reactivity';

  export const initializeSelection = (): Selection => ({
    coords: [],
    tiles: new SvelteSet(),
  });

  export const collapsing = $state<{ current: number[] }>({ current: [] });

  export const penalty = $state({ current: 0 });

  // Letter to highlight on the board, driven by the keyboard or the
  // alphabet scroll bar. Cleared when the user next touches the board.
  export const highlightLetter = $state<{ current: string | null }>({
    current: null,
  });
</script>

<script lang="ts">
  import { draggable, type DragEventData } from './draggable.svelte';
  import { fade } from 'svelte/transition';
  import { flipOverAnimation } from '~/animations.svelte';
  import { delay } from '~/animations/utils.svelte';
  import { send } from '~/animations/crossfade.svelte';

  import Tile from './Tile.svelte';
  import Flipper from '~/components/Flipper.svelte';
  import {
    isDaily,
    type Coord,
    type Selection,
    type Tile as TTile,
    type Board,
    type HighlightColors,
    isClassic,
  } from '../types';
  import Stars from './Stars.svelte';
  import {
    coordStr2Int,
    getCoordsToCheck,
    getFlick,
    getTranslate,
    isIOS,
    reverseString,
  } from './utils';
  import { onMount, tick } from 'svelte';
  import AllClear from './AllClear.svelte';
  import { dropIn } from '~/animations/dropIn';
  import { flip, flipAnimation } from '~/animations/flip.svelte';
  import { settings } from '~/modals/Settings.svelte';
  import Shadows from './Shadows.svelte';
  import { dictionary } from '~/algorithms/dictionary.svelte';
  import { animationSpeed } from '~/animations/utils.svelte';
  import { Dragging } from './boardState.svelte';
  import { getGame } from '~/store/GameContext.svelte';
  import { Game } from '~/store/game.svelte';
  import { getDimensions } from '~/utils/DimensionsContext.svelte';
  import { crossing } from '~/animations/crossfade.svelte';

  const game = $derived(getGame<Game<TTile>>().current);
  const dragging = new Dragging<TTile>();
  type Props = {
    disabled?: boolean;
    selected?: Selection;
    hideStar?: boolean;
    handleScore?: (() => Promise<boolean>) | undefined;
    handleEndTurn?: (() => Promise<boolean>) | undefined;
    allowedSwap?: [Coord, Coord];
  };
  let {
    disabled = $bindable(),
    selected = $bindable(initializeSelection()),
    allowedSwap,
    ...props
  }: Props = $props();
  let mounted = $state(false);
  onMount(() => {
    mounted = true;
    dragging.reset();
  });

  // Highlight tiles matching a letter from the keyboard or the alphabet scroll
  // bar, until the user next interacts with the board (see onpointerdown on
  // .board-inner). `highlightLetter` is module-level so external components
  // (e.g. the alphabet scroll bar) can drive it too.
  const handleKeydown = (e: KeyboardEvent) => {
    // ignore shortcuts and text entry in inputs
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const target = e.target as HTMLElement | null;
    if (
      target &&
      (target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
    ) {
      return;
    }
    if (/^[a-zA-Z]$/.test(e.key)) {
      highlightLetter.current = e.key.toLowerCase();
    } else if (e.key === 'Escape' || e.key === 'Backspace' || e.key === 'Delete') {
      highlightLetter.current = null;
    }
  };

  const tileHighlight = $derived(
    (tile: TTile): HighlightColors | undefined =>
      game.highlighted[tile.id] ??
      (highlightLetter.current &&
      tile.letter.toLowerCase().includes(highlightLetter.current)
        ? 'orange'
        : undefined),
  );

  // get board aspect ratio
  const dims = getDimensions();

  const boardPad = $derived({
    x: game.dims[0] - game.board.length,
    y: game.dims[1] - (game.board[0]?.length ?? 0),
  });

  const boardDims = $derived.by(() => {
    // width-constrained
    if (dims.board.width / dims.board.height > dims.aspect) {
      return {
        x: dims.board.width,
        y: dims.board.width / dims.aspect,
      };
    } else {
      return {
        y: dims.board.height,
        x: dims.board.height * dims.aspect,
      };
    }
  });

  const tileScale = $derived.by(() => {
    // height-constrained
    if (game.board.length / game.board[0]?.length > dims.aspect) {
      return 1 + boardPad.x / 5;
    } else {
      return 1 + boardPad.y / 5;
    }
  });

  // derived from responsive dimensions
  const tileWidth = $derived((tileScale * boardDims.x) / game.dims[0]);
  const tileHeight = $derived((tileScale * boardDims.y) / game.dims[1]);
  const tilePad = $derived(tileWidth > 60 ? 0.95 : 0.925);
  const fontSize = $derived(tileWidth * 0.4);

  const boardPadPx = $derived({
    x: (dims.board.width - tileWidth * game.board.length) / 2,
    y: (dims.board.height - tileHeight * (game.board[0]?.length ?? 0)) / 2,
  });

  const xScale = $derived(
    (i: number) => boardPadPx.x + tileWidth * (i + (1 - tilePad) / 2),
  );
  const yScale = $derived(
    (j: number) => boardPadPx.y + tileHeight * (j + (1 - tilePad) / 2),
  );

  const scale = $derived(([i, j]: Coord): Coord => [xScale(i), yScale(j)]);

  const clearSelection = () => {
    selected = initializeSelection();
    dragging.clear();
  };

  // Each press starts a new gesture. Work still running from an earlier gesture (a swap waiting
  // on its animation, the tap-return timeout) must not clear the selection or drag state that a
  // newer gesture now owns, or the second of two quick swaps gets stuck mid-drag.
  let gesture = 0;
  let swapsInFlight = 0;

  const swapTiles = async (c1: Coord, c2: Coord) => {
    if (allowedSwap) {
      const ok = ([i, j]: Coord) => allowedSwap!.some(([a, b]) => a === i && b === j);
      if (!ok(c1) || !ok(c2)) { clearSelection(); return; }
    }
    const g = gesture;
    // the ol' switcheroo
    dragging.clear();
    game.swap(c1, c2);

    const coordsToCheck = getCoordsToCheck(game.board, [c1, c2]);

    let words: Awaited<ReturnType<typeof dictionary.findWords>>['words'];
    swapsInFlight++;
    try {
      ({ words } = await dictionary.findWords(
        $state.snapshot(game.board),
        game.mode,
        coordsToCheck,
      ));

      // block further input
      if (words.length) {
        disabled = true;
        await tick();
      }

      // wait for end of swap animation
      await flipAnimation.settled();
      await tick();
    } finally {
      swapsInFlight--;
    }
    if (g === gesture) clearSelection();

    await game.save();
    if (words.length) {
      await props.handleScore?.();
    } else {
      await props.handleEndTurn?.();
    }
    await game.appendTurn([c1, c2]);
  };

  const flipTile = async ([i, j]: Coord) => {
    // the ol' switcheroo
    const board = $state.snapshot(game.board);

    // we only need to check the coords along the same axes
    const coordsToCheck = getCoordsToCheck(game.board, [[i, j], [i, j]]);

    let { words } = await dictionary.findWords(
      board,
      game.mode,
      // @ts-ignore
      coordsToCheck,
    );

    // block further input
    if (words.length) {
      disabled = true;
      await tick();
    }
    // -1 for flipping
    game.swaps--;

    // wait for end of flip animation
    await flipOverAnimation.settled();
    clearSelection();

    await game.save();
    if (words.length) {
      await props.handleScore?.();
    } else {
      await props.handleEndTurn?.();
    }
    await game.save();
    await game.appendTurn([
      [i, j],
      [i, j],
    ]);
  };

  const getHoveredTile = $derived(
    (c: { x: number; y: number }, board: Board) => {
      const [i, j] = [
        Math.round((c.x - boardPadPx.x) / tileWidth),
        Math.round((c.y - boardPadPx.y) / tileHeight),
      ];
      return {
        tile: board[i]?.[j],
        i,
        j,
      };
    },
  );

  // Presses are tracked with native pointer events: neodrag v3 only starts a drag on the first
  // pointer move and fires no callbacks for a still tap, so it only moves the tile (see onDrag).
  let pressed: TTile['id'] | undefined;

  const onPointerDown = (
    e: PointerEvent,
    id: TTile['id'],
    i: number,
    j: number,
    locked: boolean,
  ) => {
    if (locked || e.button === 2) return;
    pressed = id;
    gesture++;
    // a swap that's still animating owns the current selection, so start this gesture fresh
    if (swapsInFlight) selected = initializeSelection();
    // stupid iOS workaround
    const node = e.currentTarget as HTMLElement;
    if (isIOS()) {
      node.parentNode?.replaceChild(node, node);
    }
    // clear any letter highlight once the user touches the board
    highlightLetter.current = null;
    dragging.timestamp = Date.now();
    dragging.id = id;
    dragging.origin = [i, j];
    dragging.coords = { x: xScale(i), y: yScale(j) };
    dragging.returning.delete(id);

    selected.tiles.add(id);
    selected.coords.push([i, j].join());
  };

  // Keep a dragged tile inside the board's padding (client coords, for neodrag's `bounds`)
  const tileBounds = (node: Element) => {
    const r = node.parentElement!.getBoundingClientRect();
    return {
      left: r.left + boardPadPx.x,
      top: r.top + boardPadPx.y,
      right: r.left + dims.board.width - boardPadPx.x,
      bottom: r.top + dims.board.height - boardPadPx.y,
    };
  };

  const onDrag = async (e: DragEventData) => {
    const timestamp = Date.now();
    // unselect other selected tiles
    if (timestamp - dragging.timestamp > 100 && selected.tiles.size > 1) {
      selected.tiles = new SvelteSet([dragging.id!]);
      selected.coords = [dragging.origin!.join()];
    }

    // already clamped to the board by `tileBounds`
    dragging.coords = { ...e.offset };
    const { tile, i, j } = getHoveredTile(dragging.coords, game.board);
    penalty.current = game.getSwapPenalty(dragging.origin!, [i, j]);
    // TODO flashing on hover
    if (tile?.id !== dragging.id) {
      dragging.hovered = tile;
    } else {
      dragging.hovered = undefined;
    }
  };

  const onPointerUp = async (id: TTile['id'], i: number, j: number) => {
    if (pressed !== id) return;
    pressed = undefined;
    const g = gesture;
    const timestamp = Date.now();
    if (selected.tiles.size === 0) return;
    penalty.current = 0;

    const { i: i2, j: j2, tile } = getHoveredTile(dragging.coords!, game.board);
    if (!tile) {
      clearSelection();
      return;
    }
    const coordStr = [i2, j2].join();

    const TAP_THRESH = 250;
    const { distance, direction } = getFlick(scale([i, j]), [
      dragging.coords!.x,
      dragging.coords!.y,
    ]);
    if (
      timestamp - dragging.timestamp < TAP_THRESH &&
      selected.coords.slice(-1)[0] === coordStr
    ) {
      if (distance / tileWidth < 0.2) {
        await handleClick(i, j);
        if (dragging.id === id) {
          await tick();
          dragging.coords = { x: xScale(i), y: yScale(j) };
          dragging.returning.add(id);
          delay(animationSpeed.ms / 2).then(() => {
            dragging.returning.delete(id);
            if (g === gesture) dragging.id = undefined;
          });
        }
      } else {
        const i2 = i + direction[0];
        const j2 = j + direction[1];
        // not strictly necessary because of drag bounds
        if (i2 >= 0 && i2 < game.dims[0] && j2 >= 0 && j2 < game.dims[1]) {
          await swapTiles([i, j], [i2, j2]);
        }
      }
    } else if (
      selected.coords.length === 1 &&
      selected.coords[0] !== coordStr
    ) {
      // TODO maybe should allow long-press to select
      // selected coords is length 3 after long-press
      selected.coords.push(coordStr);
      selected.tiles.add(tile.id);
      await swapTiles([i, j], [i2, j2]);
    } else {
      await tick();
      dragging.coords = { x: xScale(i), y: yScale(j) };
      dragging.returning.add(id);
      delay(animationSpeed.ms / 2).then(() => {
        dragging.returning.delete(id);
      });
      clearSelection();
    }
    if (g === gesture) dragging.timestamp = timestamp;
  };

  const DBL_CLICK_THRESH = 500;
  let prevClick = 0;
  const handleClick = async (i: number, j: number) => {
    if (!disabled) {
      const now = +new Date();
      disabled = true;
      await tick();
      if (selected.tiles.size === 2) {
        dragging.id = undefined;
        const [i2, j2] = coordStr2Int(selected.coords[0]);
        await swapTiles([i, j], [i2, j2]);
      }
      // click same tile twice
      else if (selected.coords.length === 2) {
        const tile = game.board[i][j];
        if (now - prevClick < DBL_CLICK_THRESH) {
          await handleDoubleClick(tile, i, j);
        }
        clearSelection();
      }
      prevClick = now;
      if (!game.lost) disabled = false;
    }
  };

  const handleDoubleClick = async (tile: TTile, i: number, j: number) => {
    await flipOverAnimation.settled();
    if (tile.letter.length === 2) {
      // bumping `flips` re-keys the tile's Flipper, which animates the old letter out and the new
      // one in; a counter needs no reset afterwards (a reset could race the next flip)
      game.board[i][j] = {
        ...tile,
        flips: (tile.flips ?? 0) + 1,
        letter: reverseString(tile.letter),
      };
      await flipTile([i, j]);
    }
  };

  const tiles = $derived(
    game.board
      .flatMap((row, i) => row.map((tile, j) => ({ i, j, tile: { ...tile } })))
      .filter((t) => t.tile),
  );
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="board" class:disabled>
  <!-- skip initial resize transition -->
  {#if mounted && (dims.board || boardDims.x)}
    <div
      class="board-inner"
      style="
      --speed: {animationSpeed.ms}ms;
      width: {boardDims.x}px;
      height: {boardDims.y}px;
      font-size: {fontSize}px;
      --tile-width: {tileWidth * tilePad}px;
      --tile-height: {tileWidth * tilePad}px;
    "
    >
      {#if dragging.hovered && selected.coords.length}
        {#key dragging.hovered.id}
          <div
            class="tile-container"
            style="
            opacity: 0.5;
            {dragging.origin ? getTranslate(...scale(dragging.origin)) : ''}
          "
            in:fade={{ duration: 1000, delay: animationSpeed.ms / 3 }}
          >
            <Tile
              tile={dragging.hovered}
              size="board"
              hovered
              width={tileWidth}
            />
          </div>
        {/key}
      {/if}
      {#each tiles as { tile, i, j } (tile.id)}
        {@const c = crossing.get(tile.id)}
        {@const isAllowed = !allowedSwap || allowedSwap.some(([a, b]) => a === i && b === j)}
        <div
          data-id={tile.id}
          class="tile-container"
          class:tutorial-active={!!allowedSwap && isAllowed && !disabled}
          {@attach draggable(() => ({
            disabled: disabled || (!!allowedSwap && !isAllowed),
            bounds: tileBounds,
            onDrag,
            position:
              dragging.id === tile.id
                ? dragging.coords
                : { x: xScale(i), y: yScale(j) },
          }))}
          onpointerdown={(e) =>
            onPointerDown(e, tile.id, i, j, disabled || (!!allowedSwap && !isAllowed))}
          onpointerup={() => onPointerUp(tile.id, i, j)}
          onpointercancel={() => onPointerUp(tile.id, i, j)}
          animate:flip={{ mode: game.mode }}
          in:dropIn={{
            row: Math.max(...collapsing.current) - j,
            rowOffset: collapsing.current[i],
          }}
          out:send={{
            key: tile.id,
            scale: c.scale,
            coords: [i, j],
            index: c.index,
            origin: scale(crossing.origin),
          }}
          class:returning={dragging.returning.has(tile.id)}
          class:dragging={dragging.id === tile.id}
          class:flying={game.highlighted[tile.id] ||
          selected.tiles.has(tile.id)}
        >
          <Flipper
            key={`${tile.id}-${!!tile.flipped}-${tile.flips ?? 0}`}
            enabled={!!tile.flipped || !!tile.flips}
            reveal={!!tile.flipped || game.words === 0}
            stagger={!game.turns || game.lost ? 75 : 0}
            {i}
            {j}
          >
            <Tile
              {tile}
              size="board"
              width={tileWidth}
              selected={selected.tiles.has(tile.id)}
              hovered={tile.id === dragging.hovered?.id}
              highlighted={tileHighlight(tile)}
            />
          </Flipper>
        </div>
      {/each}
      {#each Object.entries(game.intersections) as [id, { tile, coord: [i, j] }] (id)}
        {@const c = crossing.get(id)}
        <div
          class="tile-container flying"
          style={getTranslate(...scale([i, j]))}
          data-id={id}
          in:fade
          out:send={{
            key: id,
            scale: c.scale,
            coords: [i, j],
            index: c.index,
            origin: scale(crossing.origin),
          }}
        >
          <Tile {tile} size="board" highlighted="red" width={tileWidth} />
        </div>
      {/each}
      {#if settings.shadows.current}
        <Shadows
          {xScale}
          {yScale}
          match={game.latestWord}
          highlighted={game.highlighted}
          width={tileWidth * tilePad * 0.8}
          height={tileHeight * tilePad * 0.8}
        />
      {/if}
      {#if isClassic(game)}
        <Stars pad={tilePad} {tileWidth} {tileHeight} {xScale} {yScale} />
      {/if}
    </div>
  {/if}
  <AllClear visible={isDaily(game) && !props.hideStar && game.fullClear} />
</div>

<style lang="scss">
  @use '~/theme/themify' as *;
  .board {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    position: relative;
    .board-inner {
      pointer-events: all;
      position: relative;
      margin: auto;
    }
    &.disabled :global(.tile) {
      cursor: initial;
    }
  }
  .tile-container {
    display: grid;
    position: absolute;
    z-index: 499;
    width: var(--tile-width);
    height: var(--tile-height);
    &.flying {
      transition: z-index 0s;
      z-index: 501;
    }
    &.returning {
      transition:
        z-index 0s ease calc(var(--speed) / 2),
        translate calc(var(--speed) / 2) ease;
      z-index: 500;
    }
    &::after {
      content: '';
      position: absolute;
      z-index: -1;
      width: 100%;
      height: 100%;
      transition: opacity ease var(--speed) 0.1s;
      opacity: 0;
      @include themify {
        box-shadow: 0px 8px 24px 0px themed('tile-shadow-1');
        box-shadow: 0px 16px 48px 0px themed('tile-shadow-2');
      }
    }
    &.dragging::after {
      opacity: 1;
    }
    &.tutorial-active :global(.tile) {
      animation: tutorial-glow 1.5s ease-in-out infinite;
    }
  }
  @keyframes tutorial-glow {
    0%, 100% { outline: 2px solid rgba(74, 222, 128, 0.3); }
    50% { outline: 5px solid rgba(74, 222, 128, 1); }
  }
</style>
